import { report, REPORT_TYPES } from '@adsk/exo-skeleton-api';
import {
  safeStringify,
  wrapWithEventPropertiesPrefix,
} from '@adsk/exo-skeleton-api/internal';

import {
  EXO_SKELETON_SCOPE_NAME,
  SERVICE_WORKER_CLEAN_CACHE_EVENT_NAME,
} from '../consts';
import isLocal from './isLocal';

function getServiceWorkerUrl() {
  const uniqueBuildId = process.env.APPLICATION_UNIQUE_BUILD_ID;
  const serviceWorkerUrl = `${isLocal() ? '/' : __webpack_public_path__ || '/'}service-worker.js${typeof uniqueBuildId === 'string' ? `?${uniqueBuildId}` : ''}`;
  return serviceWorkerUrl;
}

export function initializeServiceWorker({
  disabled = false,
}: { disabled?: boolean } = {}) {
  if ('serviceWorker' in navigator) {
    const serviceWorkerUrl = getServiceWorkerUrl();

    navigator.serviceWorker
      .getRegistration()
      .then((registration) => {
        if (disabled && registration) {
          return registration.unregister().then(() => {
            const message = 'service-worker-unregistered';

            return report({
              type: REPORT_TYPES.info,
              value: message,
              scope: EXO_SKELETON_SCOPE_NAME,
              context: {
                ...wrapWithEventPropertiesPrefix({
                  message,
                  type: REPORT_TYPES.info,
                }),
              },
            });
          });
        } else {
          return null;
        }
      })
      .catch((error) => {
        report({
          type: REPORT_TYPES.error,
          value: error,
          scope: EXO_SKELETON_SCOPE_NAME,
          context: {
            error,
            serviceWorkerUrl,
            ...wrapWithEventPropertiesPrefix({
              data: safeStringify({
                // error object can't be sent to DT via the event context, but it's already sent by the value parameter
                serviceWorkerUrl,
              }),
              message: error?.message,
              stack: error?.stack,
              type: REPORT_TYPES.error,
            }),
          },
        });
      });

    if (disabled) {
      return;
    }

    navigator.serviceWorker
      .register(serviceWorkerUrl, { scope: '/', updateViaCache: 'all' })
      .then(() => {
        const context = {
          serviceWorkerUrl,
        };

        const message = 'service-worker-registered';
        report({
          type: REPORT_TYPES.info,
          value: message,
          scope: EXO_SKELETON_SCOPE_NAME,
          context: {
            ...context,
            ...wrapWithEventPropertiesPrefix({
              data: safeStringify(context),
              message,
              type: REPORT_TYPES.info,
            }),
          },
        });
      })
      .catch((registrationError) => {
        report({
          type: REPORT_TYPES.error,
          value: registrationError,
          scope: EXO_SKELETON_SCOPE_NAME,
          context: {
            error: registrationError,
            serviceWorkerUrl,
            ...wrapWithEventPropertiesPrefix({
              data: safeStringify({
                // error object can't be sent to DT via the event context, but it's already sent by the value parameter
                serviceWorkerUrl,
              }),
              message: registrationError?.message,
              stack: registrationError?.stack,
              type: REPORT_TYPES.error,
            }),
          },
        });
      });
  }
}

export function clearServiceWorkerCache() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.controller?.postMessage({
      type: SERVICE_WORKER_CLEAN_CACHE_EVENT_NAME,
      message: 'please clear the cache',
    });
  }
}
